"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { GradientText, useUserYear } from "./_primitives";

export default function HeroSection() {
    const t = useTranslations();
    const year = useUserYear();

    const scrollToForm = () => {
        const el = document.getElementById("instructor-application");
        if (!el) return;
        const containers = document.querySelectorAll(
            ".overflow-y-auto, .overflow-auto, [style*='overflow']",
        );
        for (const c of containers) {
            if (c.contains(el) && c.scrollHeight > c.clientHeight) {
                const offset = 80;
                const top =
                    c.scrollTop +
                    el.getBoundingClientRect().top -
                    c.getBoundingClientRect().top -
                    offset;
                c.scrollTo({ top, behavior: "smooth" });
                return;
            }
        }
        window.scrollTo({
            top: el.getBoundingClientRect().top + window.scrollY - 80,
            behavior: "smooth",
        });
    };

    // Floating topic cards — visual signal for "votre thématique, votre territoire"
    const labelTaken = t("joinInstructor_excl_legend_taken");
    const labelAvailable = t("joinInstructor_excl_legend_available");
    const labelYou = t("joinInstructor_excl_you");

    const cards = [
        { label: "React", sub: labelTaken, tone: "muted", x: 0, y: 0, rot: -6 },
        {
            label: "Python IA",
            sub: labelTaken,
            tone: "muted",
            x: 130,
            y: -40,
            rot: 4,
        },
        {
            label: "Next.js",
            sub: labelTaken,
            tone: "muted",
            x: -10,
            y: 180,
            rot: -10,
        },
        {
            label: "Go",
            sub: labelAvailable,
            tone: "available",
            x: 165,
            y: 150,
            rot: 6,
        },
        {
            label: "Swift",
            sub: labelAvailable,
            tone: "available",
            x: 300,
            y: 60,
            rot: -3,
        },
        {
            label: "Rust",
            sub: labelYou,
            tone: "highlight",
            x: 320,
            y: 230,
            rot: 8,
        },
    ];

    return (
        <section className="relative overflow-hidden bg-white dark:bg-[#0E161D]">
            {/* soft pastel glows */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(255,122,26,0.18),transparent_60%)] blur-3xl" />
                <div className="absolute top-1/3 right-0 w-[640px] h-[640px] rounded-full bg-[radial-gradient(circle,rgba(229,21,120,0.14),transparent_60%)] blur-3xl" />
            </div>

            {/* subtle grid */}
            <div
                className="absolute inset-0 pointer-events-none opacity-60 dark:hidden"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, rgba(17,24,39,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(17,24,39,0.04) 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                    maskImage:
                        "radial-gradient(ellipse 80% 60% at 50% 40%, black 60%, transparent 100%)",
                }}
            />
            <div
                className="absolute inset-0 pointer-events-none opacity-50 hidden dark:block"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                    maskImage:
                        "radial-gradient(ellipse 80% 60% at 50% 40%, black 60%, transparent 100%)",
                }}
            />

            <div className="relative max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 lg:pt-24 pb-16 sm:pb-24 lg:pb-32">
                {/* meta header */}
                <div className="flex flex-wrap items-center justify-between gap-2 text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.28em] text-gray-400 dark:text-white/40 font-mono mb-10 sm:mb-14 lg:mb-16">
                    <span className="truncate max-w-[60%]">
                        {t("joinInstructor_hero_meta_left")}
                    </span>
                    <span>{t("joinInstructor_hero_meta_right", { year })}</span>
                </div>

                <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    {/* LEFT — text */}
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="inline-flex items-center gap-3 mb-8 sm:mb-10"
                        >
                            <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-gray-200 dark:border-white/10 bg-white/80 dark:bg-[#0E161D]/80 backdrop-blur shadow-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.25em] text-gray-700 dark:text-white/85">
                                    {t("joinInstructor_hero_badge", { year })}
                                </span>
                            </div>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="font-bold text-gray-900 dark:text-white tracking-tight leading-[0.95]"
                            style={{
                                fontFamily: "'Playfair Display', Georgia, serif",
                                fontSize: "clamp(2.6rem, 8.5vw, 7rem)",
                            }}
                        >
                            {t("joinInstructor_hero_title_part1")}
                            <br />
                            <GradientText>{t("joinInstructor_hero_title_part2")}</GradientText>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="mt-6 sm:mt-8 max-w-xl text-base sm:text-lg lg:text-xl text-gray-600 dark:text-white/70 font-light italic"
                            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                        >
                            {t("joinInstructor_hero_tagline_part1")}{" "}
                            <span className="text-primary not-italic font-semibold">
                                {t("joinInstructor_hero_tagline_part2")}
                            </span>
                        </motion.p>

                        {/* Mobile/tablet — compact cards row (visible up to lg) */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                            className="lg:hidden mt-8 grid grid-cols-3 gap-2.5 sm:gap-3"
                        >
                            <CompactCard label="React" sub={labelTaken} tone="muted" />
                            <CompactCard label="Rust" sub={labelYou} tone="highlight" />
                            <CompactCard
                                label="Go"
                                sub={labelAvailable}
                                tone="available"
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="mt-10 sm:mt-12 flex flex-wrap items-center gap-4 sm:gap-5"
                        >
                            <button
                                onClick={scrollToForm}
                                className="group relative inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-7 py-3.5 sm:py-4 rounded-xl bg-primary text-white font-semibold text-sm sm:text-base shadow-[0_8px_30px_-8px_rgba(232,81,29,0.5)] hover:shadow-[0_10px_40px_-8px_rgba(232,81,29,0.6)] transition-shadow btn-3d lip-orange"
                            >
                                <span>{t("joinInstructor_hero_cta_primary")}</span>
                                <svg
                                    className="w-4 h-4 transition-transform group-hover:translate-x-1"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                >
                                    <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" />
                                </svg>
                            </button>
                            <a
                                href="#simulator"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const el = document.getElementById("simulator");
                                    if (el)
                                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                className="text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white text-xs sm:text-sm uppercase tracking-[0.22em] sm:tracking-[0.25em] flex items-center gap-2"
                            >
                                {t("joinInstructor_hero_cta_secondary")}
                                <span className="text-primary">↓</span>
                            </a>
                        </motion.div>

                    </div>

                    {/* RIGHT — floating topic cards cluster (lg+ only) */}
                    <div className="lg:col-span-5 hidden lg:block">
                        <div className="relative h-[480px]">
                            {cards.map((c, i) => (
                                <motion.div
                                    key={c.label}
                                    initial={{ opacity: 0, y: 28, rotate: c.rot - 4 }}
                                    animate={{ opacity: 1, y: 0, rotate: c.rot }}
                                    transition={{
                                        duration: 0.8,
                                        delay: 0.4 + i * 0.08,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    style={{
                                        position: "absolute",
                                        left: c.x,
                                        top: c.y,
                                    }}
                                    className="will-change-transform"
                                >
                                    <FloatingCard
                                        label={c.label}
                                        sub={c.sub}
                                        tone={c.tone}
                                        index={i}
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function CompactCard({ label, sub, tone }) {
    const isHighlight = tone === "highlight";
    const isAvailable = tone === "available";
    return (
        <div
            className={`relative aspect-[1/1] sm:aspect-[5/4] rounded-2xl flex flex-col items-center justify-center text-center p-3 ${
                isHighlight
                    ? "bg-gradient-to-br from-[#FF7A1A] via-[#FF3B5C] to-[#E51578] text-white shadow-[0_10px_30px_-10px_rgba(232,81,29,0.4)]"
                    : isAvailable
                    ? "bg-white dark:bg-white/[0.03] border border-dashed border-primary/50"
                    : "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/10"
            }`}
        >
            {isHighlight ? (
                <div className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-white dark:bg-[#0E161D] text-primary text-[8px] uppercase tracking-[0.2em] font-bold shadow">
                    {sub}
                </div>
            ) : null}
            <span
                className={`text-base sm:text-lg font-bold tracking-tight ${
                    isHighlight ? "text-white" : "text-gray-900 dark:text-white"
                }`}
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
                {label}
            </span>
            {!isHighlight ? (
                <span
                    className={`mt-1 text-[8px] uppercase tracking-[0.22em] font-mono ${
                        isAvailable
                            ? "text-primary"
                            : "text-gray-500 dark:text-white/55"
                    }`}
                >
                    {sub}
                </span>
            ) : null}
        </div>
    );
}

function FloatingCard({ label, sub, tone, index }) {
    const isHighlight = tone === "highlight";
    const isAvailable = tone === "available";

    return (
        <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{
                duration: 4 + (index % 3),
                repeat: Infinity,
                ease: "easeInOut",
                delay: index * 0.2,
            }}
            className={`relative w-[150px] h-[150px] rounded-2xl flex flex-col items-center justify-center text-center shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] backdrop-blur ${
                isHighlight
                    ? "bg-gradient-to-br from-[#FF7A1A] via-[#FF3B5C] to-[#E51578] text-white"
                    : isAvailable
                    ? "bg-white dark:bg-[#0E161D] border border-dashed border-primary/50"
                    : "bg-white dark:bg-[#0E161D] border border-gray-200 dark:border-white/10"
            }`}
        >
            {isHighlight ? (
                <div className="absolute -top-3 -right-3 px-2.5 py-1 rounded-full bg-white dark:bg-[#0E161D] text-primary text-[9px] uppercase tracking-[0.2em] font-bold shadow-md">
                    {sub}
                </div>
            ) : null}
            <span
                className={`text-xl font-bold tracking-tight ${
                    isHighlight ? "text-white" : "text-gray-900 dark:text-white"
                }`}
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
                {label}
            </span>
            {!isHighlight ? (
                <span
                    className={`mt-2 text-[9px] uppercase tracking-[0.25em] font-mono ${
                        isAvailable ? "text-primary" : "text-gray-500 dark:text-white/55"
                    }`}
                >
                    {sub}
                </span>
            ) : null}
        </motion.div>
    );
}
